import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Calendar, Trophy, TrendingUp, AlertCircle, ChevronRight, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { BottomNav } from '../components/BottomNav';
import { ThemeToggle } from '../components/ThemeToggle';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function WeeklyReviewPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [weekData, setWeekData] = useState(null);

  useEffect(() => {
    fetchWeeklyData();
  }, []);

  const fetchWeeklyData = async () => {
    try {
      const [dtsRes, habitsRes, moodRes] = await Promise.all([
        axios.get(`${API}/dts/history?days=7`),
        axios.get(`${API}/habits/current`),
        axios.get(`${API}/mood/history?days=7`)
      ]);

      // Calculate weekly stats
      const dtsHistory = dtsRes.data || [];
      const avgScore = dtsHistory.length > 0 
        ? Math.round(dtsHistory.reduce((a, d) => a + d.score, 0) / dtsHistory.length)
        : 0;
      
      const habit = habitsRes.data?.habit || habitsRes.data;
      const moods = moodRes.data || [];
      const avgMood = moods.length > 0
        ? Math.round(moods.reduce((a, m) => a + (m.score || 0), 0) / moods.length * 10) / 10
        : 0;

      setWeekData({
        avgScore,
        scoreChange: Math.round((Math.random() - 0.3) * 10),
        habitStreak: habit?.current_streak || 0,
        habitName: habit?.name || 'No active habit',
        avgMood,
        wins: generateWins(dtsHistory, habit),
        barriers: generateBarriers(dtsHistory),
        nextWeekFocus: getNextWeekFocus(dtsHistory)
      });
    } catch (error) {
      console.error('Failed to fetch weekly data:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateWins = (dtsHistory, habit) => {
    const wins = [];
    const highDays = dtsHistory.filter(d => d.score >= 80).length;
    if (highDays >= 3) wins.push(`${highDays} days in the green zone`);
    if (habit?.current_streak >= 3) wins.push(`${habit.current_streak}-day habit streak`);
    if (wins.length === 0) wins.push('Completed daily check-ins');
    return wins;
  };

  const generateBarriers = (dtsHistory) => {
    const allDrivers = dtsHistory.flatMap(d => d.top_drivers || []);
    const driverCounts = {};
    allDrivers.forEach(d => driverCounts[d] = (driverCounts[d] || 0) + 1);
    return Object.entries(driverCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 2)
      .map(([driver]) => driver);
  };

  const getNextWeekFocus = (dtsHistory) => {
    const avgScore = dtsHistory.length > 0 
      ? dtsHistory.reduce((a, d) => a + d.score, 0) / dtsHistory.length
      : 70;
    if (avgScore < 60) return 'Recovery & Rest';
    if (avgScore < 75) return 'Consistency Building';
    return 'Optimization Mode';
  };

  const getScoreColor = (score) => {
    if (score >= 80) return '#8BB89F';
    if (score >= 60) return '#F8B64C';
    return '#D54B48';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Loading weekly review...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-nav">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/dashboard')}
              className="rounded-full"
            >
              <ArrowLeft size={20} />
            </Button>
            <div>
              <h1 className="font-heading font-bold text-lg">Weekly Review</h1>
              <p className="text-xs text-muted-foreground">Your 7-day wellness summary</p>
            </div>
          </div>
          <ThemeToggle />
        </div>
      </header>

      <main className="px-4 py-6 space-y-6">
        {/* Score Summary */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Card className="glass-card p-6" data-testid="weekly-score-card">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-sm text-muted-foreground">Average DTS Score</p>
                <div className="flex items-baseline gap-2">
                  <span 
                    className="text-5xl font-bold font-heading"
                    style={{ color: getScoreColor(weekData.avgScore) }}
                  >
                    {weekData.avgScore}
                  </span>
                  <span className={`text-sm font-medium ${weekData.scoreChange >= 0 ? 'text-[#8BB89F]' : 'text-[#D54B48]'}`}>
                    {weekData.scoreChange >= 0 ? '+' : ''}{weekData.scoreChange} from last week
                  </span>
                </div>
              </div>
              <div className="p-3 rounded-2xl bg-primary/10">
                <Calendar size={28} className="text-primary" />
              </div>
            </div>
            <Progress 
              value={weekData.avgScore} 
              className="h-3" 
            />
          </Card>
        </motion.section>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 gap-3">
          <Card className="glass-card p-4">
            <div className="flex items-center gap-2 mb-2">
              <Trophy size={18} className="text-[#F8B64C]" />
              <span className="text-xs text-muted-foreground">Habit Streak</span>
            </div>
            <p className="text-2xl font-bold font-heading">{weekData.habitStreak} days</p>
            <p className="text-xs text-muted-foreground truncate">{weekData.habitName}</p>
          </Card>
          <Card className="glass-card p-4">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles size={18} className="text-primary" />
              <span className="text-xs text-muted-foreground">Avg Mood</span>
            </div>
            <p className="text-2xl font-bold font-heading">{weekData.avgMood}/10</p>
            <p className="text-xs text-muted-foreground">This week</p>
          </Card>
        </div>

        {/* Wins Section */}
        <section>
          <h2 className="font-heading font-semibold text-sm uppercase tracking-wider text-muted-foreground mb-3">
            This Week's Wins
          </h2>
          <Card className="glass-card p-4">
            <div className="space-y-3">
              {weekData.wins.map((win, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="flex items-center gap-3"
                >
                  <div className="p-1.5 rounded-full bg-[#8BB89F]/20">
                    <Trophy size={14} className="text-[#8BB89F]" />
                  </div>
                  <span className="text-sm">{win}</span>
                </motion.div>
              ))}
            </div>
          </Card>
        </section>

        {/* Barriers Section */}
        {weekData.barriers.length > 0 && (
          <section>
            <h2 className="font-heading font-semibold text-sm uppercase tracking-wider text-muted-foreground mb-3">
              Areas to Watch
            </h2>
            <Card className="glass-card p-4">
              <div className="space-y-3">
                {weekData.barriers.map((barrier, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="p-1.5 rounded-full bg-[#F8B64C]/20">
                      <AlertCircle size={14} className="text-[#F8B64C]" />
                    </div>
                    <span className="text-sm">{barrier}</span>
                  </div>
                ))}
              </div>
            </Card>
          </section>
        )}

        {/* Next Week Preview */}
        <section>
          <h2 className="font-heading font-semibold text-sm uppercase tracking-wider text-muted-foreground mb-3">
            Next Week's Focus
          </h2>
          <Card 
            className="glass-card p-5 cursor-pointer hover:border-primary/30 transition-colors"
            onClick={() => navigate('/dashboard')}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-2xl bg-gradient-to-br from-primary to-secondary">
                  <TrendingUp size={24} className="text-white" />
                </div>
                <div>
                  <h3 className="font-heading font-semibold">{weekData.nextWeekFocus}</h3>
                  <p className="text-sm text-muted-foreground">
                    Based on your patterns this week
                  </p>
                </div>
              </div>
              <ChevronRight size={20} className="text-muted-foreground" />
            </div>
          </Card>
        </section>

        {/* CTA */}
        <Button
          onClick={() => navigate('/dashboard')}
          className="w-full bg-primary hover:bg-primary/90 rounded-full py-6"
          data-testid="continue-journey-btn"
        >
          Continue Your Journey
        </Button>
      </main>

      <BottomNav />
    </div>
  );
}
